import {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import { useReactMediaRecorder } from 'react-media-recorder';

import {
  DeleteRecordingButton,
  MicIcon,
  PauseIcon,
  PlayIcon,
  RecordAgainButton,
  RecordButton,
  VOICE_BUTTON_SIZE,
  RecordSquare,
  RecorderIdle,
  RecorderNotice,
} from '@/components/media-recorders/RecorderChrome';
import Waveform from '@/components/media-recorders/Waveform';
import { useAudioPeaks } from '@/hooks/useAudioPeaks';
import { useRecordingElapsed } from '@/hooks/useRecordingElapsed';
import { AUDIO_TIME_LIMIT_SECONDS } from '@/constants';
import { formatTime } from '@/utils/formatters/time';
import { transcodeToUploadableAudio } from '@/utils/media/audioTranscode';

type VoiceRecorderProps = {
  onRecordingStart?: () => void;
  onRecordingComplete?: (blob: Blob, url: string, duration: number) => void;
  onRecordingDelete?: () => void;
  existingVoiceUrl?: string;
  existingVoiceBlob?: Blob | null;
  existingVoiceDuration?: number;
  isActive?: boolean;
  /** Slot under the idle labels — the recording ideas link (GIFTWEB-23). */
  idleFooter?: ReactNode;
  /** Playback only — used where the saved message is shown back for review. */
  readOnly?: boolean;
};

const PERMISSION_MESSAGE =
  'We need permission to use your microphone. Allow microphone access in your browser, then tap to record.';
const NO_MIC_MESSAGE =
  "We couldn't find a microphone. Connect one and try again, or leave a written message instead.";
const GENERIC_MESSAGE =
  "We couldn't start recording. Please try again, or leave a written message instead.";

// Detect Safari / WKWebView so we can apply Safari-specific workarounds.
const isSafari =
  typeof navigator !== 'undefined' &&
  (/^((?!chrome|android).)*safari/i.test(navigator.userAgent) ||
    (/iPhone|iPad|iPod/.test(navigator.userAgent) &&
      /WebKit/.test(navigator.userAgent) &&
      !/CriOS|FxiOS|OPiOS|mercury/.test(navigator.userAgent)));

/**
 * On Safari, useReactMediaRecorder reports blob.type as "audio/wav" but the
 * actual encoded data is AAC in an MP4 container. Re-wrapping with the correct
 * MIME type lets AVFoundation identify and decode the container correctly.
 */
const correctBlobType = (blob: Blob): Blob => {
  if (!isSafari) return blob;
  if (blob.type === 'audio/wav' || blob.type === '') {
    return new Blob([blob], { type: 'audio/mp4' });
  }
  return blob;
};

/**
 * Pre-warms the AVFoundation audio session on Safari to prevent first-play
 * dropout.
 *
 * The problem:
 *   When stopStreamsOnStop releases the mic, AVFoundation transitions the
 *   session category PlayAndRecord → None. The first tap of play then
 *   triggers None → MediaPlayback. If the visible <audio> element starts
 *   decoding before that transition completes, the opening of the recording
 *   is decoded against the wrong session state and is lost.
 *
 * The fix:
 *   Load the recording into a muted temporary <audio> element, forcing
 *   AVFoundation to complete the transition and set up the AAC/MP4 pipeline
 *   before the real element plays.
 *
 * Why both a minimum delay AND canplaythrough: on fast devices
 * canplaythrough can fire before the session transition finishes (it reflects
 * buffer readiness, not session readiness), so a floor is needed; on slow
 * devices canplaythrough is the later of the two. MAX_WARMUP_MS stops the
 * user being stuck if the warm-up element errors silently.
 */
const MIN_WARMUP_MS = 1500;
const MAX_WARMUP_MS = 4000;

function warmUpSafariSession(url: string, onComplete: () => void): () => void {
  const warmup = new Audio();
  warmup.src = url;
  warmup.volume = 0;
  warmup.muted = true;

  let done = false;
  let eventFired = false;
  let timerFired = false;
  let minTimer: ReturnType<typeof setTimeout> | null = null;
  let maxTimer: ReturnType<typeof setTimeout> | null = null;

  const finish = () => {
    if (done) return;
    done = true;
    if (minTimer) clearTimeout(minTimer);
    if (maxTimer) clearTimeout(maxTimer);
    warmup.removeEventListener('canplaythrough', onEvent);
    warmup.removeEventListener('error', onEvent);
    warmup.pause();
    warmup.src = '';
    onComplete();
  };

  // Both the event and the minimum timer must fire before we proceed.
  const tryFinish = () => {
    if (eventFired && timerFired) finish();
  };

  const onEvent = () => {
    eventFired = true;
    tryFinish();
  };

  minTimer = setTimeout(() => {
    timerFired = true;
    tryFinish();
  }, MIN_WARMUP_MS);

  maxTimer = setTimeout(finish, MAX_WARMUP_MS);

  warmup.addEventListener('canplaythrough', onEvent);
  warmup.addEventListener('error', onEvent); // treat error same as ready
  warmup.load();

  return () => {
    if (done) return;
    done = true;
    if (minTimer) clearTimeout(minTimer);
    if (maxTimer) clearTimeout(maxTimer);
    warmup.removeEventListener('canplaythrough', onEvent);
    warmup.removeEventListener('error', onEvent);
    warmup.pause();
    warmup.src = '';
  };
}

export interface VoiceRecorderHandle {
  releaseStream: () => void;
}

const VoiceRecorder = forwardRef<VoiceRecorderHandle, VoiceRecorderProps>(
  function VoiceRecorder(
    {
      onRecordingStart,
      onRecordingComplete,
      onRecordingDelete,
      existingVoiceUrl,
      existingVoiceBlob,
      existingVoiceDuration,
      isActive = true,
      idleFooter,
      readOnly = false,
    },
    ref,
  ) {
    const [audioData, setAudioData] = useState<{
      url: string;
      blob: Blob | null;
    } | null>(
      existingVoiceUrl
        ? { url: existingVoiceUrl, blob: existingVoiceBlob ?? null }
        : null,
    );

    // True while the take is prepared for playback/upload — Safari session
    // warm-up, or the Chrome/Android transcode. Playback controls are withheld.
    const [isProcessing, setIsProcessing] = useState(false);
    // Set when the recording could not be turned into an uploadable format, so
    // we surface a retry rather than publish bytes iOS cannot play.
    const [processingError, setProcessingError] = useState(false);
    const [isPlaying, setIsPlaying] = useState(false);
    const [progress, setProgress] = useState(0);
    const [duration, setDuration] = useState(existingVoiceDuration ?? 0);
    // AC 3: the idle label changes once a take has been made and deleted.
    const [hasRecordedBefore, setHasRecordedBefore] = useState(
      Boolean(existingVoiceUrl),
    );

    const audioRef = useRef<HTMLAudioElement | null>(null);
    const abortWarmupRef = useRef<(() => void) | null>(null);
    const recordingRef = useRef(false);
    // Bumped whenever a take is started, deleted, or the component unmounts, so a
    // slower-than-the-user async transcode can tell it has been superseded and
    // drop its result instead of publishing a stale (or already-deleted) take.
    const jobIdRef = useRef(0);

    const peaks = useAudioPeaks(audioData?.blob ?? null);

    const { status, error, startRecording, stopRecording, clearBlobUrl } =
      useReactMediaRecorder({
        askPermissionOnMount: false,
        stopStreamsOnStop: true,
        audio: isSafari
          ? { echoCancellation: true }
          : {
              echoCancellation: true,
              noiseSuppression: true,
              autoGainControl: false,
            },
        onStop: (blobUrl, blob) => {
          const capturedDuration = elapsedRef.current;
          setDuration(capturedDuration);
          const jobId = (jobIdRef.current += 1);

          const publish = (finalBlob: Blob, finalUrl: string) => {
            setAudioData({ url: finalUrl, blob: finalBlob });
            setHasRecordedBefore(true);
            onRecordingComplete?.(finalBlob, finalUrl, capturedDuration);
          };

          if (isSafari) {
            // Safari records AAC/MP4 already — only the label is wrong.
            const correctedBlob = correctBlobType(blob);
            const correctedUrl = URL.createObjectURL(correctedBlob);
            if (correctedUrl !== blobUrl) URL.revokeObjectURL(blobUrl);

            setIsProcessing(true);
            abortWarmupRef.current = warmUpSafariSession(correctedUrl, () => {
              abortWarmupRef.current = null;
              setIsProcessing(false);
              publish(correctedBlob, correctedUrl);
            });
            return;
          }

          // Chrome/Android (and any other engine) produce WebM/Opus, which iOS
          // cannot decode. Transcode to AAC/.m4a (WAV fallback) before it is ever
          // published or uploaded — raw WebM must never leave here as a voice
          // gift. See utils/media/audioTranscode.
          setIsProcessing(true);
          setProcessingError(false);
          transcodeToUploadableAudio(blob)
            .then(({ blob: outBlob }) => {
              if (jobIdRef.current !== jobId) return; // superseded — drop it
              URL.revokeObjectURL(blobUrl);
              setIsProcessing(false);
              publish(outBlob, URL.createObjectURL(outBlob));
            })
            .catch((err) => {
              if (jobIdRef.current !== jobId) return;
              console.error('VoiceRecorder: audio transcode failed', err);
              URL.revokeObjectURL(blobUrl);
              setIsProcessing(false);
              setProcessingError(true);
            });
        },
      });

    const isRecording = status === 'recording';
    recordingRef.current = isRecording;

    const handleStop = () => {
      try {
        stopRecording();
      } catch {
        /* already stopped */
      }
    };

    const { elapsed, elapsedRef, reset: resetElapsed } = useRecordingElapsed(
      isRecording,
      AUDIO_TIME_LIMIT_SECONDS,
      handleStop,
    );

    useImperativeHandle(ref, () => ({
      releaseStream: () => {
        if (recordingRef.current) handleStop();
      },
    }));

    // Release the mic and drop any pending warm-up when the tab is switched
    // away or the page unmounts.
    useEffect(() => {
      return () => {
        abortWarmupRef.current?.();
        abortWarmupRef.current = null;
        jobIdRef.current += 1; // drop any transcode that resolves post-unmount
        if (recordingRef.current) {
          try {
            stopRecording();
          } catch {
            /* already stopped */
          }
        }
      };
      // stopRecording's identity changes with the recorder's internals; keying
      // on it would tear the microphone down mid-take.
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
      if (isActive || !recordingRef.current) return;
      handleStop();
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isActive]);

    // Adopt a recording handed in by the parent (refresh recovery).
    useEffect(() => {
      if (!existingVoiceUrl) return;
      if (audioData?.blob) return;
      if (existingVoiceUrl === audioData?.url) return;

      setAudioData({ url: existingVoiceUrl, blob: existingVoiceBlob ?? null });
      setDuration(existingVoiceDuration ?? 0);
      setHasRecordedBefore(true);
      // audioData is compared rather than depended on so the sync can't loop.
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [existingVoiceUrl, existingVoiceBlob, existingVoiceDuration]);

    // ── Handlers ──────────────────────────────────────────────────────────────

    const handleStart = () => {
      onRecordingStart?.();
      jobIdRef.current += 1; // invalidate any in-flight transcode
      resetElapsed();
      setDuration(0);
      setAudioData(null);
      setIsProcessing(false);
      setProcessingError(false);
      setIsPlaying(false);
      setProgress(0);
      startRecording();
    };

    const handlePlayPause = () => {
      const audio = audioRef.current;
      if (!audio) return;
      if (!audio.paused) {
        audio.pause();
        return;
      }
      // Older browsers return undefined rather than a promise.
      const started = audio.play();
      if (started) void started.catch(() => setIsPlaying(false));
    };

    const handleTimeUpdate = () => {
      const audio = audioRef.current;
      if (!audio || !duration) return;
      setProgress(Math.min(1, audio.currentTime / duration));
    };

    const handleDelete = () => {
      abortWarmupRef.current?.();
      abortWarmupRef.current = null;
      jobIdRef.current += 1; // invalidate any in-flight transcode

      audioRef.current?.pause();
      if (audioData?.url) URL.revokeObjectURL(audioData.url);

      setAudioData(null);
      setIsProcessing(false);
      setProcessingError(false);
      setIsPlaying(false);
      setProgress(0);
      setDuration(0);
      resetElapsed();
      clearBlobUrl();

      onRecordingDelete?.();
    };

    // ── Failure states (AC 8) ─────────────────────────────────────────────────

    const notice =
      status === 'permission_denied'
        ? PERMISSION_MESSAGE
        : status === 'no_specified_media_found'
          ? NO_MIC_MESSAGE
          : status === 'recorder_error' ||
              status === 'invalid_media_constraints' ||
              status === 'media_in_use' ||
              processingError
            ? GENERIC_MESSAGE
            : null;

    useEffect(() => {
      if (notice) console.error('VoiceRecorder:', status, error);
    }, [notice, status, error]);

    // ── Render ────────────────────────────────────────────────────────────────

    if (isRecording) {
      return (
        <div className='mt-4 flex flex-col items-center'>
          <RecordButton
            size={VOICE_BUTTON_SIZE}
            onClick={handleStop}
            label='Stop recording'
            testId='voice-stop'
          >
            <RecordSquare />
            <span className='pointer-events-none absolute inset-0 rounded-full ring-[3px] ring-[#66bda4]/40 animate-[rec-pulse_1.6s_ease-out_infinite]' />
          </RecordButton>
          <p className='mt-3.5 text-[11.5px] font-bold tracking-[0.07em] text-[#7a847d]'>
            TAP TO STOP RECORDING
          </p>
          <p
            role='timer'
            aria-label='Recording duration'
            data-testid='voice-timer'
            className='mt-0.5 text-[11.5px] font-semibold text-[#9f1239] tabular-nums'
          >
            {formatTime(elapsed)} / {formatTime(AUDIO_TIME_LIMIT_SECONDS)}
          </p>
        </div>
      );
    }

    if (isProcessing) {
      return (
        <div className='mt-4 flex flex-col items-center'>
          <RecordButton
            size={VOICE_BUTTON_SIZE}
            onClick={() => {}}
            label='Preparing your recording'
            disabled
          >
            <MicIcon />
          </RecordButton>
          <p className='mt-3.5 text-[11.5px] font-bold tracking-[0.07em] text-[#7a847d]'>
            PREPARING YOUR RECORDING…
          </p>
        </div>
      );
    }

    if (audioData?.url) {
      return (
        <div className='mt-4 flex flex-col gap-2.5'>
          {/* Contributor-recorded audio: there is no caption track to offer. */}
          {/* eslint-disable-next-line jsx-a11y/media-has-caption */}
          <audio
            ref={audioRef}
            src={audioData.url}
            preload='metadata'
            onPlay={() => setIsPlaying(true)}
            onPause={() => setIsPlaying(false)}
            onTimeUpdate={handleTimeUpdate}
            onEnded={() => {
              setIsPlaying(false);
              setProgress(0);
            }}
            className='hidden'
          />

          <div className='flex items-center gap-2.5'>
            <div className='flex min-w-0 flex-1 items-center gap-3 rounded-2xl border-[1.5px] border-[#e8e4de] bg-white px-[15px] py-[11px]'>
              <button
                type='button'
                onClick={handlePlayPause}
                aria-label={isPlaying ? 'Pause' : 'Play'}
                data-testid='voice-play'
                className='flex size-[34px] shrink-0 cursor-pointer items-center justify-center rounded-full border-none bg-[#256956] p-0'
              >
                {isPlaying ? (
                  <PauseIcon size={14} fill='#fff' />
                ) : (
                  <PlayIcon size={14} fill='#fff' />
                )}
              </button>

              <Waveform peaks={peaks} progress={progress} />

              <span
                data-testid='voice-duration'
                className='shrink-0 text-[12.5px] font-semibold text-[#72706f] tabular-nums'
              >
                {formatTime(duration)}
              </span>
            </div>

            {!readOnly && <DeleteRecordingButton onClick={handleDelete} />}
          </div>

          {!readOnly && (
            <RecordAgainButton
              onClick={() => {
                handleDelete();
                handleStart();
              }}
              className='self-start'
            />
          )}
        </div>
      );
    }

    return (
      <>
        <RecorderIdle
          label={hasRecordedBefore ? 'TAP TO RE-RECORD' : 'TAP TO START RECORDING'}
          sublabel={`${AUDIO_TIME_LIMIT_SECONDS} SECONDS MAX`}
          footer={idleFooter}
        >
          <RecordButton
            size={VOICE_BUTTON_SIZE}
            onClick={handleStart}
            label={hasRecordedBefore ? 'Record again' : 'Start recording'}
            testId='voice-record'
          >
            <MicIcon />
          </RecordButton>
        </RecorderIdle>
        {notice && <RecorderNotice>{notice}</RecorderNotice>}
      </>
    );
  },
);

export default VoiceRecorder;
